<script lang="ts" setup>
import { computed, useAttrs } from 'vue';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { AddToCartAfterSearch } from '@tools/algolia-events/AddToCartAfterSearch';
import { URL_OFFER_QUESTION } from '@tools/routes/routes';
import { useOfficeOfferStore } from '@stores/useOfficeOfferStore';
import type { LinkTarget } from '../../types/linkTarget.ts';

defineOptions({
  inheritAttrs: false,
});

// @see src/Form/Type/Deal/Contact/CreateContactButtonType.php
const props = withDefaults(defineProps<{
  hashIds: string[],
  trackingEvent?: string,
  csrfToken?: string,
  proposalId?: number,
  buyerTeamSize?: number,
  splitTeamSizes?: Record<string, number>,
  disabled?: boolean,
}>(), {
  trackingEvent: 'contact_offer',
});

const officeOfferStore = useOfficeOfferStore();

const { selectedOffersHashIds, selectedOffersTeamSizes, totalTeamSize } = storeToRefs(officeOfferStore);

const hashIds = computed<string[]>(() => {
  if (selectedOffersHashIds.value?.length > 0) {
    return selectedOffersHashIds.value;
  }

  return props.hashIds;
});

const splitTeamSizes = computed(() => {
  if (selectedOffersTeamSizes.value) {
    return selectedOffersTeamSizes.value;
  }

  const splitTeamSizesObj = props.splitTeamSizes;

  if (!splitTeamSizesObj) {
    return undefined;
  }

  return Object.entries(splitTeamSizesObj).map(([ hashId, teamSize ]) => ({
    hashId,
    teamSize,
  }));
});

const buyerTeamSize = computed(() => {
  if (totalTeamSize.value) {
    return totalTeamSize.value;
  }

  return props.buyerTeamSize;
});

const processDatalayer = (): void => {
  if (!window.tracking_offer_data) {
    return;
  }

  if (!window.dataLayer) {
    window.dataLayer = [] as Array<Record<string, unknown>>;
  }

  window.dataLayer.push({
    event: 'screenChange',
    event_action: props.trackingEvent,
    event_label: 'complete_lead',
    funnel: 'classic-question',
    step: 'contact_info',
    team_size: buyerTeamSize.value,
    ...window.tracking_offer_data,
  });
};

const processAlgolia = (): void => {
  const algoliaEvent = new AddToCartAfterSearch(hashIds.value);
  algoliaEvent.send();
};

const submit = (): boolean => {
  processDatalayer();
  processAlgolia();
  return true;
};

const attrs = useAttrs();

const btnClasses = computed<string[]>(() => {
  const classes = [];
  if (props.disabled) {
    classes.push('contact-action--disabled');
  }
  if (attrs.class) {
    classes.push(attrs.class as string);
  } else {
    classes.push('contact-action', 'btn--primary');
  }
  return classes;
});

const action = computed<string>(() => {
  let url = URL_OFFER_QUESTION;
  if (props.proposalId) {
    url += `?proposal_id=${props.proposalId}`;
  }
  return url;
});

const { isMobile } = storeToRefs(useScreensizeStore());

const target = computed<LinkTarget>(() => (isMobile.value ? '_self' : '_blank'));
</script>

<template>
  <form method="post" :action="action" :target="target" @submit="submit">
    <input
      v-for="(hashId, index) in hashIds"
      :key="hashId"
      type="hidden"
      :name="`create_contact_button[hashIds][${index}]`"
      :value="hashId"
    >
    <input
      v-if="buyerTeamSize"
      type="hidden"
      name="create_contact_button[buyerTeamSize]"
      :value="buyerTeamSize"
    >
    <template v-if="splitTeamSizes">
      <input
        v-for="splitTeamSize in splitTeamSizes"
        :key="splitTeamSize.hashId"
        type="hidden"
        :name="`create_contact_button[splitTeamSizes][${splitTeamSize.hashId}]`"
        :value="splitTeamSize.teamSize"
      >
    </template>
    <input
      v-if="csrfToken"
      type="hidden"
      name="create_contact_button[_token]"
      :value="csrfToken"
    >
    <button
      v-bind="$attrs"
      :class="btnClasses"
      :disabled="disabled"
      type="submit"
    >
      <slot />
    </button>
  </form>
</template>
